import React, { useEffect, useRef } from "react";
import $ from "jquery";
import "datatables.net-bs5";
import "datatables.net-bs5/css/dataTables.bootstrap5.css";

// ==================== UTILITY FUNCTIONS ====================

const formatDate = (dateString, type) => {
  if (type === "creation" && !dateString) return "Unknown";
  if (type === "update" && !dateString) return "Not updated";
  if (type === "commit" && !dateString) return "Not committed yet";
  if (type === "rescind" && !dateString) return "Not rescinded yet";

  if (!dateString) return "N/A";

  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatNumber = (num) => {
  return num.toLocaleString("en-US");
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case "Active":
      return "badge bg-success";
    case "Rescinded":
      return "badge bg-danger";
    case "Not Committed":
      return "badge bg-warning";
    default:
      return "badge bg-light text-dark";
  }
};

const formatChildRow = (data) => {
  return `
    <div class="p-3 bg-light border-start border-primary border-4">
      <div class="row">
        <!-- Commit Details -->
        ${
          data.orderCommitted
            ? `
          <div class="col-md-6 mb-3">
            <h6 class="text-primary fw-bold mb-2">
              <i class="fa fa-check-circle me-2"></i>Commit Details
            </h6>
            <table class="table table-sm table-borderless mb-0">
              <tr>
                <td class="text-muted" style="width: 140px;">Official Name:</td>
                <td class="fw-semibold">${data.commitOfficialFirstName} ${
                data.commitOfficialLastName
              }</td>
              </tr>
              <tr>
                <td class="text-muted">Date/Time:</td>
                <td>${formatDate(data.commitDatetime, "commit")}</td>
              </tr>
              <tr>
                <td class="text-muted align-top">Comments:</td>
                <td><em class="text-secondary">${data.commitComments}</em></td>
              </tr>
            </table>
          </div>
        `
            : `
          <div class="col-md-6 mb-3">
            <div class="alert alert-warning mb-0">
              <i class="fa fa-exclamation-triangle me-2"></i>
              Order not yet committed
            </div>
          </div>
        `
        }
        
        <!-- Rescind Details -->
        ${
          data.orderRescinded
            ? `
          <div class="col-md-6 mb-3">
            <h6 class="text-danger fw-bold mb-2">
              <i class="fa fa-times-circle me-2"></i>Rescind Details
            </h6>
            <table class="table table-sm table-borderless mb-0">
              <tr>
                <td class="text-muted" style="width: 140px;">Official Name:</td>
                <td class="fw-semibold">${data.rescindOfficialFirstName} ${
                data.rescindOfficialLastName
              }</td>
              </tr>
              <tr>
                <td class="text-muted">Date/Time:</td>
                <td>${formatDate(data.rescindedDatetime, "rescind")}</td>
              </tr>
              <tr>
                <td class="text-muted align-top">Comments:</td>
                <td><em class="text-secondary">${data.rescindComments}</em></td>
              </tr>
            </table>
          </div>
        `
            : `
          <div class="col-md-6 mb-3">
            <div class="alert alert-info mb-0">
              <i class="fa fa-info-circle me-2"></i>
              Order not rescinded
            </div>
          </div>
        `
        }
      </div>
    </div>
  `;
};

// ==================== COMPONENT ====================

const OrdersDataTable = ({ data, statusFilter }) => {
  const tableRef = useRef(null);
  const dataTableRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0) {
      // Destroy existing table if data is empty
      if (dataTableRef.current) {
        dataTableRef.current.destroy();
        dataTableRef.current = null;
      }
      return;
    }

    // Destroy existing DataTable before recreating
    if (dataTableRef.current) {
      dataTableRef.current.destroy();
      dataTableRef.current = null;
    }

    // Initialize DataTable
    dataTableRef.current = $(tableRef.current).DataTable({
      data: data,
      paging: false,
      searching: true,
      ordering: true,
      info: true,
      autoWidth: false,
      responsive: false,
      order: [[1, "asc"]], // Default sort by Order ID
      language: {
        search: "Search orders:",
        info: "Showing _TOTAL_ orders",
        infoEmpty: "No orders available",
        emptyTable:
          "No evacuation orders have been created yet. Create your first order to get started!",
        infoFiltered: "(filtered from _MAX_ total orders)",
        zeroRecords: "No matching orders found",
      },
      columnDefs: [
        {
          // Expand/Collapse column
          targets: 0,
          className: "dt-control text-center",
          orderable: false,
          data: null,
          defaultContent: "",
          width: "3%",
          render: () => "",
        },
        {
          targets: 1, // Order ID
          width: "4%",
          className: "text-center",
          data: "orderId",
          render: (data) => `<div class="fw-bold">${data}</div>`,
        },
        {
          targets: 2, // Zone ID
          width: "4%",
          className: "text-center",
          data: "zoneId",
          render: (data) => `<span class="fw-bold">${data}</span>`,
        },
        {
          targets: 3, // Event Name
          width: "15%",
          data: null,
          render: (data) => `
            <div class="fw-bold">${data.eventName}</div>
            <small class="text-muted">${data.eventType}</small><br/>
            <small class="text-muted">By: ${data.createdBy}</small>
          `,
        },
        {
          targets: 4, // Muster Station
          width: "15%",
          data: null,
          render: (data) => `
            <div class="fw-bold">${data.musterStationName}</div>
            <small class="text-muted">${data.musterStation} - ${data.zoneName}</small>
          `,
        },
        {
          targets: 5, // Total People
          className: "text-end",
          data: "totalPeople",
          render: (data) => `
            <span>${data}</span>
            <div><small class="text-muted">People</small></div>
          `,
        },
        {
          targets: 6, // Total Vehicles
          className: "text-end",
          data: "totalEvacVeh",
          render: (data) => `
            <span>${data}</span>
            <div><small class="text-muted">Vehicles</small></div>
          `,
        },
        {
          targets: 7, // Total Dwellings
          className: "text-end",
          data: "totalDwellings",
          render: (data) => `
            <span>${data}</span>
            <div><small class="text-muted">Dwellings</small></div>
          `,
        },
        {
          targets: 8, // Addresses
          className: "text-end",
          data: "affectedAddresses",
          render: (data) => `
            <span>${formatNumber(data)}</span>
            <div><small class="text-muted">addresses</small></div>
          `,
        },
        {
          targets: 9, // Status
          className: "text-center",
          data: "status",
          render: (data) =>
            `<span class="${getStatusBadgeClass(data)}">${data}</span>`,
        },
        {
          targets: 10, // Creation Date
          width: "10%",
          className: "text-center",
          data: "createdAt",
          render: (data) => `<small>${formatDate(data, "creation")}</small>`,
        },
        {
          targets: 11, // Committed Timestamp
          className: "text-center",
          data: "commitDatetime",
          render: (data) => `<small>${formatDate(data, "commit")}</small>`,
        },
        {
          targets: 12, // Rescinded Timestamp
          className: "text-center",
          data: "rescindedDatetime",
          render: (data) => `<small>${formatDate(data, "rescind")}</small>`,
        },
        {
          targets: 13, // Actions
          className: "text-center",
          orderable: false,
          data: null,
          render: (data, type, row) => {
            return `
              <a href="/evacuation-portal-panels/evacuation-order-report?zoneId=${row.zoneId}&eventId=${row.eventId}" 
                class="btn btn-sm btn-outline-primary" 
                title="View Order Details">
                <i class="fa fa-eye"></i>
              </a>
            `;
          },
        },
      ],
      initComplete: function () {
        // Apply status filter if provided
        if (statusFilter) {
          this.api().column(9).search(statusFilter).draw();
        }
      },
    });

    // Handle row expansion
    $(tableRef.current).on("click", "td.dt-control", function () {
      const tr = $(this).closest("tr");
      const row = dataTableRef.current.row(tr);

      if (row.child.isShown()) {
        row.child.hide();
        tr.removeClass("shown");
      } else {
        row.child(formatChildRow(row.data())).show();
        tr.addClass("shown");
      }
    });

    // Cleanup
    return () => {
      if (dataTableRef.current) {
        $(tableRef.current).off("click", "td.dt-control");
        dataTableRef.current.destroy();
        dataTableRef.current = null;
      }
    };
  }, [data, statusFilter]);

  // Apply filter when statusFilter changes
  useEffect(() => {
    if (dataTableRef.current) {
      dataTableRef.current.column(9).search(statusFilter).draw();
    }
  }, [statusFilter]);

  return (
    <>
      {/* Custom DataTable Styles */}
      <style>
        {`
          #ordersTable thead th {
            text-align: center !important;
            vertical-align: middle !important;
          }
          
          #ordersTable thead th > div {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 4px;
          }
          
          #ordersTable thead th .sorting,
          #ordersTable thead th .sorting_asc,
          #ordersTable thead th .sorting_desc {
            display: flex !important;
            align-items: center;
            justify-content: center;
          }
          
          #ordersTable thead th .sorting::before,
          #ordersTable thead th .sorting::after,
          #ordersTable thead th .sorting_asc::before,
          #ordersTable thead th .sorting_asc::after,
          #ordersTable thead th .sorting_desc::before,
          #ordersTable thead th .sorting_desc::after {
            margin: 0 !important;
          }

          #ordersTable thead {
            background-color: var(--bs-dark) !important;
            color: white !important;
          }

          #ordersTable thead th {
            background-color: var(--bs-dark) !important;
            color: white !important;
            border-color: var(--bs-gray-800) !important;
          }

          .dataTables_filter {
            margin-bottom: 0.25rem;
          }

          .dataTables_filter label {
            font-family: 'Open Sans', sans-serif;
            font-weight: 600 !important;
            margin-right: 0.5rem;
          }

          .dataTables_filter input {
            margin-left: 0.5em;
            padding: 0.375rem 0.75rem;
            border: 1px solid #ced4da;
            border-radius: 0.25rem;
          }
        `}
      </style>

      <div className="table-responsive">
        <table
          ref={tableRef}
          id="ordersTable"
          className="table table-striped table-bordered align-middle w-100"
        >
          <thead className="table-dark">
            <tr>
              <th>
                <div></div>
              </th>
              <th>
                <div>Order ID</div>
              </th>
              <th>
                <div>Zone ID</div>
              </th>
              <th>
                <div>Event Name</div>
              </th>
              <th>
                <div>Muster Station</div>
              </th>
              <th>
                <div>Total People</div>
              </th>
              <th>
                <div>Total Vehicles</div>
              </th>
              <th>
                <div>Total Dwellings</div>
              </th>
              <th>
                <div>Total Addresses</div>
              </th>
              <th>
                <div>Status</div>
              </th>
              <th>
                <div>Creation Timestamp</div>
              </th>
              <th>
                <div>Committed Date-Time</div>
              </th>
              <th>
                <div>Rescinded Date-Time</div>
              </th>
              <th>
                <div>Actions</div>
              </th>
            </tr>
          </thead>
          <tbody></tbody>
        </table>
      </div>
    </>
  );
};

export default OrdersDataTable;
